import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Clock, 
  ChevronRight, 
  ShieldCheck, 
  Search, 
  Star, 
  User,
  Home,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import axiosInstance from "../../../../api/axiosInstance";

// Component Removed

const VendorCard = ({ vendor, onViewDetails }) => {
  // Safe name parsing based on provided JSON structure
  const nameParts = vendor.name ? vendor.name.split(" ") : ["Expert"];
  const fName = vendor.firstName || nameParts[0];
  const lName = vendor.lastName || nameParts.slice(1).join(" ");
  const vendorImg = vendor.image || vendor.profileImage || vendor.avatar;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -4 }}
      className="group bg-white/80 backdrop-blur-xl border border-slate-200/60 rounded-[2.5rem] p-5 md:p-7 shadow-sm hover:shadow-2xl transition-all duration-500 mb-5 overflow-hidden relative"
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/50 rounded-full -translate-y-16 translate-x-16 blur-3xl group-hover:bg-indigo-100/50 transition-colors duration-500"></div>
      
      <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
        <div className="relative">
          <div className="h-24 w-24 md:h-28 md:w-28 rounded-[2rem] overflow-hidden bg-slate-100 flex items-center justify-center border-4 border-white shadow-inner ring-1 ring-slate-100">
            {vendorImg ? (
              <img 
                src={vendorImg} 
                alt={fName} 
                className="w-full h-full object-cover transition-transform duration-700"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200"; }}
              />
            ) : (
              <User size={40} className="text-slate-300" />
            )}
          </div>
          <div className="absolute bottom-1 right-1 w-5 h-5 bg-emerald-500 border-4 border-white rounded-full shadow-sm animate-pulse"></div>
        </div>

        <div className="flex-1 text-center md:text-left">
          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-3">
            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 font-outfit tracking-tight capitalize">
              {fName} {lName}
            </h3>
            <div className="flex items-center justify-center md:justify-start gap-2">
              {(vendor.documentsOverallStatus === "Verified" || vendor.isVerified) && (
                <div className="flex items-center gap-1 bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border border-blue-100">
                  <ShieldCheck size={12} className="fill-blue-100" />
                  Verified
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mb-4">
            <div className="flex items-center gap-1.5 text-slate-500 text-sm font-medium">
              <Clock size={16} className="text-indigo-500" />
              <span>{vendor.experienceYears || vendor.experience || 0} Years Exp.</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-500 text-sm font-medium">
              <Star size={16} className="text-amber-500 fill-amber-500" />
              <span>4.9 (120+ Reviews)</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-center md:justify-start">
            {["Expertise", "Certified"].map((tag) => (
              <span key={tag} className="text-[10px] font-bold text-slate-400 bg-slate-50 px-2 py-1 rounded-md border border-slate-100 uppercase tracking-widest">
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-row md:flex-col items-center justify-between md:justify-center gap-6 w-full md:w-auto pt-6 md:pt-0 border-t md:border-t-0 md:border-l border-slate-100 md:pl-8">
          <div className="text-left md:text-center">
            <div className="flex items-baseline gap-1">
             
            </div>
          </div>

          <button
            onClick={() => onViewDetails(vendor._id || vendor.id)}
            className="group/btn relative overflow-hidden flex items-center gap-3 bg-[#134DA7] text-white font-bold py-4 px-8 rounded-2xl shadow-xl transition-all active:scale-95"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500"></div>
            <span className="relative z-10">Consult Expert</span>
            <ChevronRight size={18} className="relative z-10 group-hover/btn:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const ServiceDetails = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDescOpen, setIsDescOpen] = useState(false);

  const handleViewDetails = (vendorId) => {
    navigate(`/vendor-details/${vendorId}`, { state: { serviceId } });
  };

  useEffect(() => {
    const fetchServiceDetails = async () => {
      try {
        const response = await axiosInstance.get(`/users/services/${serviceId}/vendors`);
        setService(response.data.service || null);
        setVendors(response.data.vendors || []);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServiceDetails();
  }, [serviceId]);

  if (loading) return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-slate-50">
      <div className="w-20 h-20 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
      <p className="mt-4 text-slate-400 font-bold text-xs uppercase tracking-widest animate-pulse">Gathering Experts...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col font-outfit">
      
      {/* Dark Hero Section matching CategoryDetails */}
      <section className="relative pt-24 pb-24 px-6 bg-[#020617] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=2000" 
            alt="service-hero" 
            className="w-full h-full object-cover"
          />
        </div>

        <div className="container mx-auto relative z-10 max-w-4xl text-center">
          {/* Breadcrumbs */}
          <nav className="flex items-center justify-center gap-2 text-sm font-medium text-slate-300 mb-8">
            <button onClick={() => navigate('/home')} className="hover:text-white transition-colors flex items-center gap-1">
              <Home size={14} /> Home
            </button>
            <ChevronRight size={14} className="opacity-50" />
            <button onClick={() => navigate('/services')} className="hover:text-white transition-colors">
              Services
            </button>
            <ChevronRight size={14} className="opacity-50" />
            <span className="text-white">{service?.title || "Loading..."}</span>
          </nav>

          <h1 className="text-3xl md:text-5xl font-bold text-white mb-6">
            {service?.title || "Professional Services"}
          </h1>
        </div>
      </section>

      <div className="container mx-auto px-6 pb-32 max-w-6xl relative z-10 flex-grow py-10">
        
        {/* About This Service Accordion (Flush Alignment) */}
        {service?.description && (
          <div className="mb-12">
            <button 
              onClick={() => setIsDescOpen(!isDescOpen)}
              className="w-full flex items-center justify-between group cursor-pointer outline-none border-b border-gray-200 pb-4"
            >
              <h2 className="text-xl font-bold text-gray-800 tracking-tight flex items-center gap-2">
                <div className="w-1.5 h-6 bg-[#134DA7] rounded-full"></div>
                About This Service
              </h2>
              <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-gray-100 transition-colors">
                {isDescOpen ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
              </div>
            </button>
            
            <AnimatePresence>
              {isDescOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="prose prose-slate max-w-none text-gray-600 leading-relaxed text-base pt-6 pb-2">
                    {/* Handling line breaks properly for long text walls */}
                    {service.description.split('\n').map((paragraph, idx) => (
                      <p key={idx} className="mb-4 last:mb-0">{paragraph}</p>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Section Divider */}
        <div className="mb-8 flex items-center justify-between border-b border-gray-200 pb-4">
          <h2 className="text-xl font-bold text-gray-800 tracking-tight flex items-center gap-2">
            <div className="w-1.5 h-6 bg-[#0097b2] rounded-full"></div>
            Our Top Rated Experts
          </h2>
          <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
            {vendors.length} {vendors.length === 1 ? "Expert" : "Experts"} Available
          </span>
        </div>

        <div className="space-y-6">
          <AnimatePresence>
            {vendors.length === 0 ? (
              <div className="bg-white rounded-3xl p-16 text-center shadow-sm border border-gray-100 mt-12">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search size={32} className="text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">No Specialists Found</h3>
                <p className="text-gray-500 mb-8 max-w-md mx-auto">We couldn't find any verified experts for this service at the moment. Please check back later.</p>
                <button 
                  onClick={() => navigate('/services')} 
                  className="bg-[#134DA7] hover:bg-[#0097b2] transition-colors text-white text-sm font-semibold py-3 px-8 rounded-xl"
                >
                  Explore All Services
                </button>
              </div>
            ) : (
              vendors.map((vendor) => (
                <VendorCard key={vendor._id || vendor.id} vendor={vendor} onViewDetails={handleViewDetails} />
              ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetails;